import { useState, useEffect, useMemo, Suspense } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Agent, LogEntry, InboxItem, TimelineEvent, TERMINAL_THEMES } from "./types";
import { AgentSwitcher } from "./components/AgentSwitcher";
import { ChatOnlyPage } from "./components/ChatOnlyPage";
import { CommandPalette, PaletteCommand } from "./components/CommandPalette";
import { QuickAskWidget } from "./components/QuickAskWidget";
import { agentScopedUrl, agentScopedEventSource, fetchFleet } from "./lib/fleetClient";
import { getCachedIdToken } from "./lib/firebaseAuth";
import { AppContext, AppContextValue } from "./AppContext";
import {
  ROUTES, NAV_SECTIONS, TAB_TO_PATH, PATH_TO_TAB, DEFAULT_TAB, BRAND_ICON, ExecTab, RouteSpec,
} from "./routes";
import {
  ChevronLeft, ChevronRight, Menu, X, Command, Globe, AlertTriangle, Sliders,
} from "lucide-react";
import { FreshnessBadge, ConnState } from "./components/primitives/FreshnessBadge";
import { Toaster } from "./components/ui/sonner";

// Action ids that require a specific agent in scope. When the user is in
// fleet-overview mode (selectedAgentId === null) and a peer is configured,
// these are disabled with an explainer banner — write-on-implicit-agent[0]
// was the §2.1 bug and the data-bleed concern Terrell explicitly named.
const WRITE_ACTIONS: ReadonlySet<string> = new Set([
  "send_inbound", "release_outbound", "kill_outbound", "edit_doc",
  "delete_doc", "create_doc", "status_toggle", "preview_generate",
  "review_mark", "calendar_create", "calendar_delete",
  "admin_exec",
]);

interface CapabilityShape {
  schema_version: number;
  agent: { name: string; role: string; model: string; agent_home: string };
  skills: Array<{ id: string; name: string; description: string }>;
  tasks: string[];
  allowed_tabs: string[];
  allowed_actions: string[];
  allowed_tiles: string[];
  override_present: boolean;
  known: { tabs: string[]; actions: string[]; tiles: string[] };
}

/**
 * AppShell — the persistent dashboard chrome.
 *
 * Owns ALL global state that previously lived in the App() god-component:
 * the SSE/EventSource connections, the refresh polling, scopedFetch, the
 * selected-agent scope, theme, sidebar UI, capabilities, the command palette,
 * and the quick-ask widget. These are mounted ONCE here and never unmount on
 * route changes — the router only swaps what renders inside <Outlet/>, so SSE
 * never reconnects and polling never duplicates when the operator navigates.
 *
 * The shell publishes everything route pages need through AppContext.
 */
export default function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();

  // Derive the active tab id from the URL path (basename-stripped by router).
  const activePath = location.pathname.replace(/^\//, "").split("/")[0] || "";
  const activeTab: ExecTab = PATH_TO_TAB[activePath] || DEFAULT_TAB;

  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [inbox, setInbox] = useState<InboxItem[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [hardeningAudit, setHardeningAudit] = useState<any>(null);
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [capabilities, setCapabilities] = useState<CapabilityShape | null>(null);
  // SSE connection state — drives the topbar FreshnessBadge.
  const [connState, setConnState] = useState<ConnState>("reconnecting");
  // Gate SSE on the Firebase ID token being cached (see the poll effect below).
  const [authTokenReady, setAuthTokenReady] = useState<boolean>(() => getCachedIdToken() != null);

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() =>
    localStorage.getItem("plumbus-sidebar-collapsed") === "true"
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  useEffect(() => {
    localStorage.setItem("plumbus-sidebar-collapsed", String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Per-section collapse state — persists in localStorage.
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem("plumbus-sidebar-collapsed-sections");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  const toggleSection = (label: string) => {
    setCollapsedSections((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      localStorage.setItem("plumbus-sidebar-collapsed-sections", JSON.stringify(next));
      return next;
    });
  };

  const [selectedThemeId, setSelectedThemeId] = useState<string>(
    () => localStorage.getItem("plumbus-terminal-theme") || "office_executive"
  );
  const activeTheme = TERMINAL_THEMES.find((t) => t.id === selectedThemeId) || TERMINAL_THEMES[0];
  useEffect(() => {
    localStorage.setItem("plumbus-terminal-theme", selectedThemeId);
  }, [selectedThemeId]);

  // Bridge the active TerminalTheme → the design-system CSS token vars so the
  // shadcn primitives + tokens follow the THEME SELECTOR (light/dark/amber/etc.)
  // instead of a hardcoded dark palette. Overriding the `--color-*` @theme vars
  // flows to both Tailwind utilities and shadcn var() use.
  const isDarkTheme = (() => {
    const h = (activeTheme.bg || "#000").replace("#", "");
    const n = h.length === 3 ? h.split("").map((c) => c + c).join("") : h.padEnd(6, "0");
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
  })();
  useEffect(() => {
    const root = document.documentElement;
    const t = activeTheme;
    const vars: Record<string, string> = {
      "--color-background": t.bg, "--color-foreground": t.fg,
      "--color-card": t.inputBg, "--color-card-foreground": t.fg,
      "--color-popover": t.inputBg, "--color-popover-foreground": t.fg,
      "--color-primary": t.accent, "--color-primary-foreground": t.bg,
      "--color-accent": t.bannerBg, "--color-accent-foreground": t.fg,
      "--color-muted": t.bannerBg, "--color-muted-foreground": t.systemText,
      "--color-border": t.border, "--color-input": t.border, "--color-ring": t.accent,
    };
    for (const k in vars) root.style.setProperty(k, vars[k]);
    root.classList.toggle("dark", isDarkTheme);
  }, [activeTheme, isDarkTheme]);

  // §2.1 fix: do NOT silently fall back to agents[0] when scope is null
  // in a multi-agent fleet. But the local agent is always implicit
  // alongside any peers — fetchFleet prepends self with _localOnly=true.
  const localAgent = agents.find((a) => (a as any)._localOnly === true) || null;
  const isSingleAgentMode = agents.length >= 1 && agents.every((a) => (a as any)._localOnly === true);
  const selectedAgent = selectedAgentId
    ? agents.find((a) => a.id === selectedAgentId)
    : localAgent;
  const selectedIsLocalOnly = !!(selectedAgent && (selectedAgent as any)._localOnly);
  const isFleetOverview = !isSingleAgentMode && selectedAgentId === null && !localAgent;
  const scopedAgentId = (selectedIsLocalOnly || selectedAgentId === null) ? null : selectedAgentId;

  const scopedFetch = useMemo(
    () =>
      (path: string, init?: RequestInit) =>
        fetch(agentScopedUrl(scopedAgentId, path.replace(/^\/api/, "")), init),
    [scopedAgentId]
  );

  const refreshAllState = async () => {
    try {
      const [fleetAgents, resLog, resIn, resTl, resHd] = await Promise.all([
        fetchFleet(),
        fetch(agentScopedUrl(scopedAgentId, "/logs")),
        fetch(agentScopedUrl(scopedAgentId, "/inbox")),
        fetch(agentScopedUrl(scopedAgentId, "/timeline?limit=200")),
        fetch(agentScopedUrl(scopedAgentId, "/hardening/audit")),
      ]);
      setAgents(fleetAgents);
      const logsJson = await resLog.json().catch(() => []);
      const inboxJson = await resIn.json().catch(() => []);
      const timelineJson = await resTl.json().catch(() => []);
      const hardJson = await resHd.json().catch(() => null);
      setLogs(Array.isArray(logsJson) ? logsJson : []);
      setInbox(Array.isArray(inboxJson) ? inboxJson : []);
      setTimeline(Array.isArray(timelineJson) ? timelineJson : []);
      setHardeningAudit(hardJson);
    } catch (_) {
      console.warn("backend initializing…");
    }
  };

  // SSE + initial refresh — re-subscribes only when the agent scope changes.
  // Wait for the Firebase ID token to be cached before opening the SSE live
  // channel. AuthGate renders the app on user-present (sync onAuthStateChanged),
  // but the token cache is filled by a separate async onIdTokenChanged — so on a
  // cold load the EventSource could open tokenless, 401 over the Funnel, and stay
  // "stale" (EventSource keeps retrying the same tokenless URL). Poll briefly
  // until the token is warm; the SSE effect re-runs when this flips true.
  useEffect(() => {
    if (authTokenReady) return;
    const id = window.setInterval(() => {
      if (getCachedIdToken() != null) {
        setAuthTokenReady(true);
        window.clearInterval(id);
      }
    }, 200);
    return () => window.clearInterval(id);
  }, [authTokenReady]);

  // Mounted once in the shell so navigation never reconnects/duplicates.
  useEffect(() => {
    setLogs([]);
    setInbox([]);
    setTimeline([]);
    setHardeningAudit(null);
    refreshAllState();
    setConnState("reconnecting");
    // Hold off until the token is cached, else SSE 401s over the Funnel and
    // never recovers. Re-runs when authTokenReady flips (it's in the deps).
    if (!authTokenReady) return;
    const evtLog = agentScopedEventSource(scopedAgentId, "/events");
    evtLog.onopen = () => setConnState("live");
    evtLog.onerror = () =>
      setConnState((s) => (s === "live" ? "reconnecting" : "stale"));
    evtLog.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "log") setLogs((prev) => [...prev, payload.data]);
        else if (payload.type === "inbox") setInbox(payload.data);
      } catch (_) {}
    };
    const evtTimeline = agentScopedEventSource(scopedAgentId, "/timeline/stream");
    evtTimeline.addEventListener("timeline", (e: MessageEvent) => {
      try {
        const payload = JSON.parse(e.data);
        const incoming: any[] = Array.isArray(payload?.events) ? payload.events : [];
        if (incoming.length === 0) return;
        setTimeline((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          const fresh = incoming.filter((x) => x?.id && !seen.has(x.id));
          return fresh.length === 0 ? prev : [...prev, ...fresh];
        });
      } catch (_) {}
    });
    return () => {
      evtLog.close();
      evtTimeline.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedAgentId, authTokenReady]);

  // Global Cmd+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleToggleStatus = async (agentId: string, currentStatus: string) => {
    const rowAgent = agents.find((a) => a.id === agentId);
    const rowIsLocalOnly = !!(rowAgent && (rowAgent as any)._localOnly);
    const routeAgentId = rowIsLocalOnly ? null : agentId;
    const nextStatus = currentStatus === "online" ? "offline" : "online";
    try {
      const res = await fetch(agentScopedUrl(routeAgentId, `/agents/${agentId}/status`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: nextStatus }),
      });
      const data = await res.json();
      if (data.success) {
        setAgents((prev) =>
          prev.map((a) => (a.id === agentId ? { ...a, status: nextStatus } : a))
        );
      }
    } catch (_) {}
  };

  const handleTriggerInbound = async (payload: any): Promise<boolean> => {
    if (isFleetOverview) {
      console.warn("send blocked: select a specific agent first");
      return false;
    }
    try {
      const res = await fetch(agentScopedUrl(scopedAgentId, "/inbound"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data && (data.success || data.queued)) {
        refreshAllState();
        return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  };

  // Capabilities — load once the auth token is ready, and reload on scope
  // change. Gated on authTokenReady (like the SSE effect below) so a cold
  // load doesn't fire tokenless and 401. Critically, a transient failure
  // (e.g. a 401 while the Firebase token is mid-refresh, or a backend blip)
  // must NOT blank a previously-good policy: nulling capabilities flips every
  // write action off via canDo() and grays out the chat input until a full
  // reload. So we keep last-known-good on failure and retry the initial load
  // with backoff.
  useEffect(() => {
    if (!authTokenReady) return;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      try {
        const res = await fetch(agentScopedUrl(scopedAgentId, "/capabilities"));
        if (!res.ok) throw new Error(`capabilities HTTP ${res.status}`);
        const data = (await res.json()) as CapabilityShape;
        if (!cancelled) setCapabilities(data);
      } catch {
        // Preserve last-known-good capabilities; only retry while we have none.
        if (!cancelled && attempt < 4) {
          attempt += 1;
          timer = setTimeout(load, 1000 * attempt);
        }
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [scopedAgentId, authTokenReady]);

  const allowedTabsSet = useMemo<Set<string>>(() => {
    if (!capabilities || !Array.isArray(capabilities.allowed_tabs) || capabilities.allowed_tabs.length === 0) {
      return new Set(ROUTES.map((n) => n.id));
    }
    return new Set(capabilities.allowed_tabs);
  }, [capabilities]);

  const allowedActionsSet = useMemo<Set<string>>(() => {
    if (!capabilities) return new Set();
    return new Set(capabilities.allowed_actions || []);
  }, [capabilities]);

  const canDo = (action: string): boolean => {
    // Fleet Overview is intentionally read-only.
    if (WRITE_ACTIONS.has(action) && isFleetOverview) return false;
    // capabilities === null means the /capabilities fetch hasn't succeeded yet
    // (cold start) or transiently failed — the policy is UNKNOWN, not "deny".
    // This is an advisory, single-operator UI gate (the backend does not
    // enforce it), so an unknown policy must NOT disable the operator's own
    // actions, or the chat input grays out until a full reload. Apply the real
    // policy only once it's actually loaded.
    if (capabilities === null) return true;
    if (allowedActionsSet.size === 0) {
      return !WRITE_ACTIONS.has(action);
    }
    return allowedActionsSet.has(action);
  };

  const visibleNavItems = useMemo<RouteSpec[]>(
    () => ROUTES.filter((n) => allowedTabsSet.has(n.id)),
    [allowedTabsSet]
  );

  // Navigate by tab id — the bridge for legacy goToTab(tabId) call sites.
  const goToTab = (tabId: string) => {
    const path = TAB_TO_PATH[tabId];
    if (path) navigate(`/${path}`);
  };

  const paletteCommands = useMemo<PaletteCommand[]>(() => {
    const tabCmds: PaletteCommand[] = visibleNavItems.map((n) => ({
      id: `tab-${n.id}`,
      label: `Open ${n.label}`,
      kind: "tab",
      action: () => navigate(`/${n.path}`),
    }));
    // Reports sub-tabs are managed inside ReportsPage now; the palette just
    // deep-links to /reports (parity with prior behavior, where reportsSubTab
    // was set but ReportsPage owns its own sub-tab state).
    const reportsSubCmds: PaletteCommand[] = (
      [
        ["hardening",   "Reports → Hardening scrubber"],
        ["audit",       "Reports → Audit DB browser"],
        ["api-usage",   "Reports → API usage"],
        ["improvement", "Reports → Self-improvement"],
      ] as Array<[string, string]>
    ).map(([id, label]) => ({
      id: `report-${id}`,
      label,
      kind: "report",
      action: () => navigate("/reports"),
    }));
    const agentCmds: PaletteCommand[] = [
      { id: "agent-all", label: "Switch to: All agents (local process)", kind: "agent", action: () => setSelectedAgentId(null) },
      ...agents.map((a) => ({
        id: `agent-${a.id}`,
        label: `Switch to: ${a.name}`,
        kind: "agent" as const,
        action: () => setSelectedAgentId(a.id),
      })),
    ];
    const themeCmds: PaletteCommand[] = TERMINAL_THEMES.map((t) => ({
      id: `theme-${t.id}`,
      label: `Theme: ${t.name}`,
      kind: "theme",
      action: () => setSelectedThemeId(t.id),
    }));
    return [...tabCmds, ...reportsSubCmds, ...agentCmds, ...themeCmds];
  }, [agents, visibleNavItems, navigate]);

  const ctxValue: AppContextValue = {
    agents,
    selectedAgentId,
    setSelectedAgentId,
    selectedAgent,
    scopedAgentId,
    isSingleAgentMode,
    isFleetOverview,
    logs,
    inbox,
    timeline,
    hardeningAudit,
    refreshAllState,
    scopedFetch,
    connState,
    theme: activeTheme,
    selectedThemeId,
    setSelectedThemeId,
    canDo,
    onToggleStatus: handleToggleStatus,
    onTriggerInbound: handleTriggerInbound,
    goToTab,
  };

  // ─── Chat-only mode ──────────────────────────────────────────────────
  // When served at /chat or /chat/<...> (Tailscale-companion mini-app),
  // render the minimal ChatOnlyPage instead of the full dashboard.
  const chatOnly =
    typeof window !== "undefined" && window.location.pathname.startsWith("/chat");
  if (chatOnly) {
    return (
      <ChatOnlyPage
        theme={activeTheme}
        agents={agents}
        selectedAgentId={selectedAgentId}
        onSelectAgent={setSelectedAgentId}
        scopedFetch={scopedFetch}
        canSend={true}
        selectedThemeId={selectedThemeId}
        onSelectTheme={setSelectedThemeId}
      />
    );
  }

  const Brand = BRAND_ICON;

  return (
    <AppContext.Provider value={ctxValue}>
      <div
        style={{ backgroundColor: activeTheme.bg, color: activeTheme.fg }}
        className="h-screen font-sans flex flex-col md:flex-row select-none overflow-hidden"
      >
        {isMobileSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        <aside
          style={{
            borderColor: activeTheme.border,
            backgroundColor: activeTheme.inputBg,
          }}
          className={`md:relative md:flex md:translate-x-0 md:h-screen md:shrink-0 flex-col border-r overflow-hidden ${
            isMobileSidebarOpen
              ? "fixed inset-y-0 left-0 z-50 flex w-64 transition-transform duration-200"
              : "hidden"
          } ${isSidebarCollapsed ? "md:w-16" : "md:w-60"}`}
        >
          {/* brand */}
          <div
            style={{ borderBottomColor: activeTheme.border }}
            className="h-16 flex items-center justify-between px-3 border-b shrink-0"
          >
            <div className="flex items-center gap-2 overflow-hidden">
              <div
                style={{ backgroundColor: `${activeTheme.accent}15`, borderColor: `${activeTheme.accent}30` }}
                className="p-1.5 border rounded-md flex-shrink-0"
              >
                <Brand size={16} style={{ color: activeTheme.accent }} />
              </div>
              {(!isSidebarCollapsed || isMobileSidebarOpen) && (
                <div className="truncate">
                  <span className="font-sans font-bold text-sm tracking-tight" style={{ color: activeTheme.fg }}>
                    Margo Dock
                  </span>
                  <span className="text-[10px] block leading-none mt-0.5" style={{ color: activeTheme.systemText }}>
                    Leah · JAG marketing ops
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              style={{ color: activeTheme.systemText, borderColor: activeTheme.border }}
              className="hidden md:flex items-center justify-center h-6 w-6 border rounded cursor-pointer"
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isSidebarCollapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
            </button>
            <button
              onClick={() => setIsMobileSidebarOpen(false)}
              style={{ color: activeTheme.accent }}
              className="md:hidden flex items-center justify-center h-6 w-6"
            >
              <X size={16} />
            </button>
          </div>

          {/* agent switcher */}
          {(!isSidebarCollapsed || isMobileSidebarOpen) && (
            <div className="p-3">
              <AgentSwitcher
                agents={agents}
                selectedAgentId={selectedAgentId}
                onSelect={setSelectedAgentId}
                theme={activeTheme}
              />
            </div>
          )}

          {/* nav — grouped IA with collapsible sections */}
          <nav className="flex-1 px-2 py-2 space-y-1 overflow-y-auto">
            {NAV_SECTIONS.map((section) => {
              const sectionItems = visibleNavItems.filter((n) =>
                section.items.includes(n.id)
              );
              if (sectionItems.length === 0) return null;

              const isCollapsed = !!collapsedSections[section.label];

              return (
                <div key={section.label} className="mb-2">
                  {(!isSidebarCollapsed || isMobileSidebarOpen) && (
                    <button
                      onClick={() => toggleSection(section.label)}
                      style={{ color: activeTheme.systemText }}
                      className="flex w-full items-center justify-between text-[10px] font-bold uppercase tracking-widest px-2 py-1 mb-1 mt-1 hover:bg-white/5 rounded transition-colors cursor-pointer select-none"
                      title={isCollapsed ? "Expand section" : "Collapse section"}
                    >
                      <span>{section.label}</span>
                      <span className="text-[9px] opacity-60">
                        {isCollapsed ? "▸" : "▾"}
                      </span>
                    </button>
                  )}
                  {!isCollapsed &&
                    sectionItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <NavLink
                          key={item.id}
                          to={`/${item.path}`}
                          onClick={() => setIsMobileSidebarOpen(false)}
                          title={item.label}
                          style={({ isActive }) => ({
                            backgroundColor: isActive ? `${activeTheme.accent}15` : "transparent",
                            color: isActive ? activeTheme.accent : activeTheme.systemText,
                            borderColor: isActive ? `${activeTheme.accent}33` : "transparent",
                          })}
                          className={`flex items-center gap-3 w-full py-2 px-3 border font-sans rounded text-sm font-semibold cursor-pointer transition-all ${
                            isSidebarCollapsed && !isMobileSidebarOpen ? "justify-center" : "justify-start"
                          }`}
                        >
                          <Icon size={15} className="flex-shrink-0" />
                          {(!isSidebarCollapsed || isMobileSidebarOpen) && <span className="truncate">{item.label}</span>}
                        </NavLink>
                      );
                    })}
                </div>
              );
            })}
            {(!isSidebarCollapsed || isMobileSidebarOpen) && (
              <div style={{ borderTopColor: activeTheme.border }} className="pt-3 mt-4 border-t space-y-2">
                <div
                  style={{ color: activeTheme.systemText }}
                  className="text-[10px] font-bold uppercase tracking-widest px-2"
                >
                  Theme
                </div>
                <div style={{ backgroundColor: activeTheme.bg, borderColor: activeTheme.border }} className="flex items-center gap-2 p-2 border rounded">
                  <Sliders size={12} style={{ color: activeTheme.accent }} />
                  <select
                    value={selectedThemeId}
                    onChange={(e) => setSelectedThemeId(e.target.value)}
                    style={{ color: activeTheme.fg }}
                    className="bg-transparent border-none text-[11px] font-semibold focus:ring-0 focus:outline-none cursor-pointer w-full"
                  >
                    {TERMINAL_THEMES.map((t) => (
                      <option key={t.id} value={t.id} style={{ backgroundColor: t.bg, color: t.fg }}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={() => setIsPaletteOpen(true)}
                  style={{ color: activeTheme.accent, borderColor: activeTheme.border, backgroundColor: activeTheme.bg }}
                  className="text-[11px] px-2 py-1.5 border rounded cursor-pointer flex items-center justify-between w-full font-semibold"
                >
                  <span className="flex items-center gap-1.5"><Command size={12} /> Command palette</span>
                  <span style={{ color: activeTheme.systemText }} className="text-[9px] font-mono">⌘K</span>
                </button>
              </div>
            )}
          </nav>

          {/* footer */}
          <div
            style={{ borderTopColor: activeTheme.border }}
            className="p-3 border-t shrink-0"
          >
            {(!isSidebarCollapsed || isMobileSidebarOpen) ? (
              <div className="text-[10px]" style={{ color: activeTheme.systemText }}>
                <div className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  <span className="font-semibold">SSE live · {agents.length} agent{agents.length === 1 ? "" : "s"}</span>
                </div>
                <div className="text-[9px] mt-1 font-mono opacity-75">
                  {selectedAgent ? `scoped: ${selectedAgent.name}` : "no agents"}
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" title="SSE live" />
              </div>
            )}
          </div>
        </aside>

        {/* MAIN */}
        <div className="flex-1 flex flex-col min-w-0 h-screen">
          <header
            style={{ borderBottomColor: activeTheme.border, backgroundColor: activeTheme.inputBg }}
            className="h-14 border-b px-4 flex items-center justify-between shrink-0"
          >
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                style={{ color: activeTheme.accent, borderColor: activeTheme.border }}
                className="md:hidden flex items-center justify-center h-8 w-8 border rounded cursor-pointer shrink-0"
              >
                <Menu size={16} />
              </button>
              <h1
                className="text-sm font-bold tracking-tight truncate"
                style={{ color: activeTheme.fg }}
              >
                {visibleNavItems.find((n) => n.id === activeTab)?.label || "Workspace"}
              </h1>
              {selectedAgent ? (
                <span
                  style={{
                    color: activeTheme.accent,
                    backgroundColor: `${activeTheme.accent}10`,
                    borderColor: `${activeTheme.accent}33`,
                  }}
                  className="hidden sm:inline-flex items-center gap-1.5 border rounded px-2 py-0.5 text-[10px] font-bold font-mono truncate max-w-[40vw]"
                  title={`scope: ${selectedAgent.name} @ ${selectedAgent.homeDir} · ${selectedAgent.channels?.length || 0} channel(s)`}
                >
                  <Globe size={10} />
                  {selectedAgent.name}
                  <span style={{ color: activeTheme.systemText }} className="font-normal">
                    · {selectedAgent.channels?.length || 0}ch
                  </span>
                </span>
              ) : (
                <span
                  style={{
                    color: "#f59e0b",
                    backgroundColor: "#f59e0b15",
                    borderColor: "#f59e0b55",
                  }}
                  className="hidden sm:inline-flex items-center gap-1 border rounded px-2 py-0.5 text-[10px] font-bold font-mono"
                  title="No specific agent in scope — write actions are disabled. Pick an agent in the sidebar."
                >
                  <AlertTriangle size={10} />
                  Fleet Overview · read-only
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <FreshnessBadge state={connState} theme={activeTheme} />
              <span style={{ color: activeTheme.systemText }} className="hidden md:inline">
                {new Date().toUTCString().slice(0, 22)} UTC
              </span>
            </div>
          </header>

          {isFleetOverview && agents.length > 1 && (
            <div
              style={{
                backgroundColor: "#f59e0b10",
                borderBottomColor: "#f59e0b40",
                color: "#b45309",
              }}
              className="border-b px-4 py-1.5 text-[11px] flex items-center gap-2 shrink-0"
            >
              <AlertTriangle size={12} />
              <span>
                <strong>Fleet Overview</strong> — read-only. Pick a specific agent in the sidebar to send messages, toggle status, edit docs, or take any other write action.
              </span>
            </div>
          )}

          <main className="flex-1 p-4 md:p-6 overflow-y-auto">
            <Suspense
              fallback={
                <div style={{ color: activeTheme.systemText }} className="text-[11px] font-mono p-4">
                  loading…
                </div>
              }
            >
              <Outlet />
            </Suspense>
          </main>
        </div>

        <CommandPalette
          open={isPaletteOpen}
          onClose={() => setIsPaletteOpen(false)}
          commands={paletteCommands}
          theme={activeTheme}
        />

        {/* Global Gemini Flash quick-ask widget */}
        <QuickAskWidget theme={activeTheme} scopedFetch={scopedFetch} />

        {/* Design-system toast surface (Sonner), token-styled */}
        <Toaster position="bottom-right" richColors closeButton theme={isDarkTheme ? "dark" : "light"} />
      </div>
    </AppContext.Provider>
  );
}
